// Phase 15 slice 1 — Settings (Account / Course / Users / Integrations).

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/clerk-react';
import { LogOut } from 'lucide-react';
import type { UserRole } from '@greeniq/shared';
import { ApiError, api } from '../api/client.js';
import { useMe } from '../context/MeContext.js';
import { PageHeader } from '../components/ui/page-header';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';

const ADMIN_ROLES: UserRole[] = ['ageye_admin', 'superintendent', 'ownership'];
const VALID_TABS = ['account', 'course', 'users', 'integrations'] as const;
type SettingsTab = (typeof VALID_TABS)[number];

function tabFromParams(value: string | null): SettingsTab {
  return VALID_TABS.includes((value ?? '') as SettingsTab) ? (value as SettingsTab) : 'account';
}

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = tabFromParams(searchParams.get('tab'));

  return (
    <div className="max-w-5xl p-6 md:p-10">
      <PageHeader
        eyebrow="Phase 15"
        title="Settings"
        description="Your account, course profile, team access, and integration status."
      />

      <Tabs
        value={active}
        onValueChange={(v) => setSearchParams((p) => { p.set('tab', v); return p; }, { replace: true })}
        className="mt-8"
      >
        <TabsList>
          <TabsTrigger value="account">Account</TabsTrigger>
          <TabsTrigger value="course">Course</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
        </TabsList>
        <TabsContent value="account">
          <AccountTab />
        </TabsContent>
        <TabsContent value="course">
          <CourseTab />
        </TabsContent>
        <TabsContent value="users">
          <UsersTab />
        </TabsContent>
        <TabsContent value="integrations">
          <IntegrationsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** Account tab — matches the YardIQ/Tools house style: custom card driven by
 *  `useUser()`, initials-fallback avatar, and a single Sign out CTA. No
 *  embedded Clerk widgets and no `openUserProfile()` modal — siblings just
 *  show what Clerk already exposes through hooks. */
function AccountTab() {
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  if (!isLoaded || !user) {
    return <div className="mt-6 text-sm text-fg-muted">Loading…</div>;
  }
  const displayName =
    user.fullName ||
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.primaryEmailAddress?.emailAddress ||
    'Your account';
  const initials = (user.firstName?.[0] ?? '') + (user.lastName?.[0] ?? '');

  return (
    <div className="mt-6 flex flex-col gap-4">
      <div className="rounded-lg border border-border bg-bg-card p-5 flex items-center gap-4">
        <div className="h-16 w-16 rounded-full bg-greeniq-500 text-[#0b1014] flex items-center justify-center text-xl font-display font-bold">
          {(initials || displayName.slice(0, 2)).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-xl font-semibold text-fg truncate">{displayName}</h3>
          <p className="text-sm text-fg-muted truncate">
            {user.primaryEmailAddress?.emailAddress ?? 'No email on file'}
          </p>
          {user.primaryPhoneNumber?.phoneNumber && (
            <p className="text-sm text-fg-muted truncate">{user.primaryPhoneNumber.phoneNumber}</p>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-bg-card p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h4 className="font-display text-sm font-semibold text-fg uppercase tracking-wider">
              Sign out
            </h4>
            <p className="mt-1 text-sm text-fg-muted">
              Ends your session on this device.
            </p>
          </div>
          <Button variant="outline" onClick={() => signOut()}>
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}

interface CourseSettings {
  id: number;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  timezone: string;
  primaryIrrigationVendor: string | null;
  waterRightGallonsPerYear: string | number | null;
  acreageTotal: string | number | null;
  foundedYear: number | null;
  constructionType: string | null;
}

function CourseTab() {
  const { courseSlug } = useParams();
  const { me } = useMe();
  const role: UserRole | undefined = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;
  const canEdit = role ? ADMIN_ROLES.includes(role) : false;
  const [course, setCourse] = useState<CourseSettings | null>(null);
  const [draft, setDraft] = useState<CourseSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!courseSlug) return;
    (async () => {
      const r = await api.get<{ course: CourseSettings }>(`/courses/${courseSlug}/settings`);
      setCourse(r.course);
      setDraft(r.course);
    })();
  }, [courseSlug]);

  async function save() {
    if (!courseSlug || !draft) return;
    setSaving(true);
    try {
      const r = await api.patch<{ course: CourseSettings }>(`/courses/${courseSlug}/settings`, draft);
      setCourse(r.course);
      setDraft(r.course);
      setMessage('Saved.');
      setTimeout(() => setMessage(null), 2500);
    } catch (err) {
      setMessage(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  if (!draft || !course) return <p className="text-fg-muted">Loading…</p>;

  return (
    <div className="max-w-2xl">
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Course name" value={draft.name} disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, name: v })} />
        <Field label="Address" value={draft.address ?? ''} disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, address: v || null })} />
        <Field label="City" value={draft.city ?? ''} disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, city: v || null })} />
        <Field label="State" value={draft.state ?? ''} disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, state: v || null })} />
        <Field label="ZIP" value={draft.zip ?? ''} disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, zip: v || null })} />
        <Field label="Timezone (IANA)" value={draft.timezone} disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, timezone: v })} />
        <Field label="Irrigation vendor" value={draft.primaryIrrigationVendor ?? ''} disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, primaryIrrigationVendor: v || null })} />
        <Field label="Acreage total" value={String(draft.acreageTotal ?? '')} disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, acreageTotal: v ? Number(v) : null })} type="number" />
        <Field label="Founded year" value={String(draft.foundedYear ?? '')} disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, foundedYear: v ? Number(v) : null })} type="number" />
        <Field label="Construction type" value={draft.constructionType ?? ''} disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, constructionType: v || null })} />
        <Field
          label="Water right (gallons/year)"
          value={String(draft.waterRightGallonsPerYear ?? '')}
          disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, waterRightGallonsPerYear: v || null })}
          type="number"
        />
      </div>
      {canEdit && (
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={() => void save()}
            disabled={saving}
            className="px-4 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-sm font-semibold hover:bg-greeniq-400 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {message && <span className="text-sm text-fg-muted">{message}</span>}
        </div>
      )}
    </div>
  );
}

function Field({
  label, value, onChange, disabled, type = 'text',
}: {
  label: string; value: string; onChange: (v: string) => void; disabled: boolean; type?: string;
}) {
  return (
    <label className="text-xs">
      {label}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="mt-1 w-full border border-border rounded-md py-2 px-2 text-sm disabled:bg-bg-elevated"
      />
    </label>
  );
}

interface UserRow {
  id: number;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  lastSeenAt: string | null;
  role: string;
  status: string;
  grantedAt: string;
}

interface InvitationRow {
  id: number;
  email: string;
  role: string;
  token: string;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
}

function UsersTab() {
  const { courseSlug } = useParams();
  const { me } = useMe();
  const role: UserRole | undefined = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;
  const canManage = role ? ADMIN_ROLES.includes(role) : false;
  const [users, setUsers] = useState<UserRow[]>([]);
  const [invitations, setInvitations] = useState<InvitationRow[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('assistant_super');
  const [message, setMessage] = useState<string | null>(null);

  async function load() {
    if (!courseSlug) return;
    const r = await api.get<{ users: UserRow[]; invitations: InvitationRow[] }>(
      `/courses/${courseSlug}/settings/users`,
    );
    setUsers(r.users);
    setInvitations(r.invitations);
  }

  useEffect(() => {
    void load();
  }, [courseSlug]);

  async function invite() {
    if (!courseSlug) return;
    try {
      const r = await api.post<{ invitation: InvitationRow }>(
        `/courses/${courseSlug}/settings/invitations`,
        { email: inviteEmail, role: inviteRole },
      );
      setInviteEmail('');
      setMessage(`Invitation token: ${r.invitation.token} (expires ${new Date(r.invitation.expiresAt).toLocaleDateString()})`);
      void load();
    } catch (err) {
      setMessage(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function revoke(userId: number) {
    if (!courseSlug) return;
    if (!confirm('Revoke this user\'s access to the course?')) return;
    await api.delete(`/courses/${courseSlug}/settings/users/${userId}`);
    await load();
  }

  return (
    <div>
      <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">Team ({users.length})</h2>
      <div className="mt-3 overflow-auto bg-bg-card rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border bg-bg-elevated">
              <th className="px-3 py-2 font-semibold text-fg-muted">Name</th>
              <th className="px-3 py-2 font-semibold text-fg-muted">Email</th>
              <th className="px-3 py-2 font-semibold text-fg-muted">Role</th>
              <th className="px-3 py-2 font-semibold text-fg-muted">Last seen</th>
              {canManage && <th className="px-3 py-2"></th>}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border last:border-b-0">
                <td className="px-3 py-2">{`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || '—'}</td>
                <td className="px-3 py-2 text-fg-muted">{u.email ?? '—'}</td>
                <td className="px-3 py-2 capitalize">{u.role.replace(/_/g, ' ')}</td>
                <td className="px-3 py-2 text-xs text-fg-muted">
                  {u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleString() : 'never'}
                </td>
                {canManage && (
                  <td className="px-3 py-2 text-xs">
                    <button onClick={() => void revoke(u.id)} className="text-[color:var(--color-error)] hover:underline">
                      Revoke
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canManage && (
        <div className="mt-6">
          <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">Invite a user</h2>
          <div className="mt-3 flex items-end gap-2 flex-wrap">
            <label className="text-xs flex-1 min-w-[200px]">
              Email
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="mt-1 w-full border border-border rounded-md py-2 px-2 text-sm"
                placeholder="teammate@example.com"
              />
            </label>
            <label className="text-xs">
              Role
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="mt-1 border border-border rounded-md py-2 px-2 text-sm"
              >
                <option value="superintendent">Superintendent</option>
                <option value="assistant_super">Assistant super</option>
                <option value="committee">Committee</option>
                <option value="ownership">Ownership</option>
                <option value="crew">Crew</option>
              </select>
            </label>
            <button
              onClick={() => void invite()}
              disabled={!inviteEmail}
              className="px-3 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-sm font-semibold hover:bg-greeniq-400 disabled:opacity-50"
            >
              Send invitation
            </button>
          </div>
          {message && <div className="mt-3 p-3 rounded-md bg-bg-elevated text-xs text-fg break-all">{message}</div>}
        </div>
      )}

      {invitations.length > 0 && (
        <div className="mt-8">
          <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">Recent invitations</h2>
          <div className="mt-3 overflow-auto bg-bg-card rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-border bg-bg-elevated">
                  <th className="px-3 py-2 font-semibold text-fg-muted">Email</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted">Role</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted">Status</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted">Expires</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((i) => (
                  <tr key={i.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2">{i.email}</td>
                    <td className="px-3 py-2 capitalize">{i.role.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2">
                      {i.acceptedAt ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] bg-success-muted text-[color:var(--color-success)]">Accepted</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] bg-warning-muted text-[color:var(--color-warning)]">Pending</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-fg-muted">{new Date(i.expiresAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

interface IntegrationRow {
  key: string;
  label: string;
  configured: boolean;
  category: string;
}

function IntegrationsTab() {
  const { courseSlug } = useParams();
  const [integrations, setIntegrations] = useState<IntegrationRow[]>([]);

  useEffect(() => {
    if (!courseSlug) return;
    (async () => {
      const r = await api.get<{ integrations: IntegrationRow[] }>(
        `/courses/${courseSlug}/settings/integrations`,
      );
      setIntegrations(r.integrations);
    })();
  }, [courseSlug]);

  const byCategory = new Map<string, IntegrationRow[]>();
  for (const i of integrations) {
    const arr = byCategory.get(i.category) ?? [];
    arr.push(i);
    byCategory.set(i.category, arr);
  }

  return (
    <div>
      <p className="text-sm text-fg-muted">
        Read-only view of which external services are configured on this server. Contact AGEYE admin to provision missing keys.
      </p>
      <div className="mt-4 space-y-4">
        {Array.from(byCategory.entries()).map(([cat, rows]) => (
          <div key={cat}>
            <h3 className="text-xs uppercase tracking-wider text-fg-muted font-semibold">{cat.replace(/_/g, ' ')}</h3>
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              {rows.map((r) => (
                <div
                  key={r.key}
                  className="p-3 bg-bg-card rounded-lg border border-border flex items-center justify-between"
                >
                  <span className="text-sm font-semibold">{r.label}</span>
                  {r.configured ? (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success-muted text-[color:var(--color-success)]">Configured</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-bg-elevated text-fg-secondary">Not set</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
